import { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { 
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CameraIcon,
  VideoCameraIcon,
  EyeIcon
} from '@heroicons/react/24/outline'
import type { Shoot } from '../types'

interface MiniCalendarWidgetProps {
  shoots: Shoot[]
  loading?: boolean
}

const MiniCalendarWidget = ({ shoots, loading }: MiniCalendarWidgetProps) => {
  const navigate = useNavigate()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)

  // Get current month's calendar data
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days = []
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day))
    }
    
    return days
  }

  // Get shoots for a specific date
  const getShootsForDate = (date: Date) => {
    if (!date) return []
    // Use local timezone date formatting to avoid timezone offset issues
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const dateString = `${year}-${month}-${day}`
    return shoots.filter(shoot => shoot.date === dateString)
  }

  // Navigate months
  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const newDate = new Date(prev)
      newDate.setMonth(prev.getMonth() + (direction === 'next' ? 1 : -1))
      return newDate
    })
  }

  // Get upcoming shoots (next 7 days)
  const getUpcomingShootsSummary = () => {
    const today = new Date()
    const nextWeek = new Date(today)
    nextWeek.setDate(today.getDate() + 7)
    
    return shoots.filter(shoot => {
      const shootDate = new Date(shoot.date + 'T00:00:00')
      return shootDate >= today && shootDate <= nextWeek
    }).slice(0, 3) // Show max 3 upcoming shoots
  }

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString + 'T00:00:00')
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(today.getDate() + 1)
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today'
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow'
    } else {
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric' 
      })
    }
  }

  const days = getDaysInMonth(currentDate)
  const upcomingShootsSummary = getUpcomingShootsSummary()
  const today = new Date()

  if (loading) {
    return (
      <div className="glass-mockup rounded-xl sm:rounded-2xl p-4 sm:p-6">
        <div className="animate-pulse">
          <div className="h-5 sm:h-6 bg-white/10 rounded mb-3 sm:mb-4"></div>
          <div className="grid grid-cols-7 gap-1 mb-3 sm:mb-4">
            {Array.from({ length: 35 }).map((_, i) => (
              <div key={i} className="h-6 sm:h-8 bg-white/5 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="glass-mockup rounded-xl sm:rounded-2xl p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
          <h3 className="text-white font-semibold text-base sm:text-lg">Calendar</h3>
        </div>
        <button
          onClick={() => navigate('/calendar')}
          className="flex items-center space-x-1 text-blue-400 hover:text-blue-300 transition-colors text-xs sm:text-sm"
        >
          <EyeIcon className="w-3 h-3 sm:w-4 sm:h-4" />
          <span className="hidden sm:inline">View Full</span>
          <span className="sm:hidden">Full</span>
        </button>
      </div>

      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <button
          onClick={() => navigateMonth('prev')}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors touch-manipulation"
        >
          <ChevronLeftIcon className="w-4 h-4 text-gray-400" />
        </button>
        
        <h4 className="text-white font-medium text-sm sm:text-base">
          {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h4>
        
        <button
          onClick={() => navigateMonth('next')}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors touch-manipulation"
        >
          <ChevronRightIcon className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="mb-4 sm:mb-6">
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => (
            <div key={index} className="text-center text-xs text-gray-400 font-medium py-1">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar days */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => {
            if (!day) {
              return <div key={index} className="h-6 sm:h-8"></div>
            }
            
            const dayShots = getShootsForDate(day)
            const isToday = day.toDateString() === today.toDateString()
            const isSelected = selectedDate?.toDateString() === day.toDateString()
            const hasShots = dayShots.length > 0
            
            return (
              <motion.button
                key={index}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedDate(isSelected ? null : day)}
                className={`
                  h-6 sm:h-8 rounded-lg text-xs font-medium transition-all relative touch-manipulation
                  ${isToday 
                    ? 'bg-blue-500 text-white' 
                    : isSelected
                    ? 'bg-white/20 text-white'
                    : hasShots
                    ? 'bg-purple-500/30 text-purple-300 hover:bg-purple-500/40'
                    : 'text-gray-400 hover:bg-white/10 hover:text-white'
                  }
                `}
              >
                {day.getDate()}
                {hasShots && (
                  <div className="absolute bottom-0.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-current rounded-full"></div>
                )}
              </motion.button>
            )
          })}
        </div>
      </div>

      {/* Selected Date Details */}
      {selectedDate && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mb-3 sm:mb-4 p-3 bg-white/5 rounded-lg border border-white/10"
        >
          <h5 className="text-white font-medium mb-2 text-sm sm:text-base">
            {selectedDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'long', 
              day: 'numeric' 
            })}
          </h5>
          {getShootsForDate(selectedDate).length > 0 ? (
            <div className="space-y-2">
              {getShootsForDate(selectedDate).map(shoot => (
                <div key={shoot.id} className="flex items-center space-x-2 text-xs sm:text-sm">
                  {shoot.type === 'photo' ? (
                    <CameraIcon className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400 flex-shrink-0" />
                  ) : (
                    <VideoCameraIcon className="w-3 h-3 sm:w-4 sm:h-4 text-purple-400 flex-shrink-0" />
                  )}
                  <span className="text-white truncate flex-1">{shoot.title}</span>
                  <span className="text-gray-400 flex-shrink-0">•</span>
                  <span className="text-gray-400 flex-shrink-0">{formatTime(shoot.start_time)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-xs sm:text-sm">No shoots scheduled</p>
          )}
        </motion.div>
      )}

      {/* Upcoming Shoots Summary */}
      <div>
        <h5 className="text-white font-medium mb-2 sm:mb-3 text-sm">Next 7 Days</h5>
        {upcomingShootsSummary.length > 0 ? (
          <div className="space-y-2">
            {upcomingShootsSummary.map(shoot => (
              <motion.div
                key={shoot.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center space-x-2 sm:space-x-3 p-2 rounded-lg hover:bg-white/5 transition-colors"
              >
                <div className="flex-shrink-0">
                  {shoot.type === 'photo' ? (
                    <CameraIcon className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />
                  ) : (
                    <VideoCameraIcon className="w-3 h-3 sm:w-4 sm:h-4 text-purple-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-xs sm:text-sm font-medium truncate">{shoot.title}</p>
                  <div className="flex items-center space-x-1 sm:space-x-2 text-xs text-gray-400">
                    <span>{formatDate(shoot.date)}</span>
                    <span>•</span>
                    <span>{formatTime(shoot.start_time)}</span>
                  </div>
                </div>
              </motion.div>
            ))}
            
            {shoots.filter(shoot => {
              const shootDate = new Date(shoot.date + 'T00:00:00')
              const nextWeek = new Date(today)
              nextWeek.setDate(today.getDate() + 7)
              return shootDate >= today && shootDate <= nextWeek
            }).length > 3 && (
              <button
                onClick={() => navigate('/calendar')}
                className="w-full p-2 text-center text-blue-400 hover:text-blue-300 text-xs sm:text-sm transition-colors"
              >
                +{shoots.filter(shoot => {
                  const shootDate = new Date(shoot.date + 'T00:00:00')
                  const nextWeek = new Date(today)
                  nextWeek.setDate(today.getDate() + 7)
                  return shootDate >= today && shootDate <= nextWeek
                }).length - 3} more this week
              </button>
            )}
          </div>
        ) : (
          <div className="text-center py-3 sm:py-4">
            <CalendarIcon className="w-6 h-6 sm:w-8 sm:h-8 text-gray-500 mx-auto mb-2" />
            <p className="text-gray-400 text-xs sm:text-sm">No upcoming shoots</p>
            <button
              onClick={() => navigate('/new-shoot')}
              className="text-blue-400 hover:text-blue-300 text-xs mt-1 transition-colors"
            >
              Schedule a shoot
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default MiniCalendarWidget 